import {useMemo} from 'react'

import {useCustomerSavedLocations} from 'src/customers/saved-locations/use-saved-locations'
import {useJob} from 'src/jobs/use-jobs'

export type LocationOption = {
    value: string
    label: string
}

type UseDeployableLocationsOptions = {
    jobId: string | null
    customerId: string
    enabled: boolean
}

export function useDeployableLocations({
    jobId,
    customerId,
    enabled,
}: UseDeployableLocationsOptions) {
    const jobQuery = useJob(jobId ?? '', {
        enabled: enabled && jobId != null,
    })
    const savedLocationsQuery = useCustomerSavedLocations(customerId, undefined, {enabled})

    const options = useMemo(() => {
        // Dedupe by location id and by normalized name.
        const seenLocationIds = new Set<string>()
        const seenNames = new Set<string>()
        const locationOptions: LocationOption[] = []
        const add = (locationId: string, name: string) => {
            const nameKey = name.trim().toLowerCase()
            if (seenLocationIds.has(locationId) || seenNames.has(nameKey)) {
                return
            }
            seenLocationIds.add(locationId)
            seenNames.add(nameKey)
            locationOptions.push({value: locationId, label: name})
        }

        for (const location of jobQuery.data?.locations ?? []) {
            add(location.locationId, location.name)
        }
        for (const savedLocation of savedLocationsQuery.data ?? []) {
            add(savedLocation.locationId, savedLocation.name)
        }

        return locationOptions
    }, [jobQuery.data?.locations, savedLocationsQuery.data])

    return {
        options,
        isLoading: jobQuery.isLoading || savedLocationsQuery.isLoading,
    }
}
